/**
 * Project registry for the demo app (Figma-style multiple projects).
 *
 * A "project" is a named workspace. Its actual canvases (the editor's multi-canvas
 * tab set) persist independently under a per-project localStorage key, via the
 * library's `persistenceKey` prop — so each project is a full, self-contained
 * editor with its own pages. This module only tracks the lightweight registry
 * (id / name / type / timestamps) used to render the home "Your projects" grid.
 */

export interface Project {
  id: string
  name: string
  diagramType: string
  createdAt: number
  updatedAt: number
}

const REGISTRY_KEY = 'gv2-projects'

/** localStorage key holding a project's canvas tab set. */
export const canvasKeyFor = (id: string) => `gv2-canvas-${id}`

const genId = () => `p-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`

export function loadProjects(): Project[] {
  try {
    const raw = localStorage.getItem(REGISTRY_KEY)
    if (raw) {
      const arr = JSON.parse(raw) as Project[]
      if (Array.isArray(arr)) return arr.slice().sort((a, b) => b.updatedAt - a.updatedAt)
    }
  } catch {
    /* ignore corrupt registry */
  }
  return []
}

function saveProjects(projects: Project[]) {
  try {
    localStorage.setItem(REGISTRY_KEY, JSON.stringify(projects))
  } catch {
    /* ignore quota errors */
  }
}

export function createProject(name: string, diagramType = 'architecture'): Project {
  const now = Date.now()
  const project: Project = {
    id: genId(),
    name: name.trim() || 'Untitled project',
    diagramType,
    createdAt: now,
    updatedAt: now,
  }
  saveProjects([project, ...loadProjects()])
  return project
}

/** Merge a patch into a project and bump updatedAt. No-op if the id is unknown. */
export function touchProject(id: string, patch: Partial<Project> = {}) {
  const projects = loadProjects()
  const idx = projects.findIndex((p) => p.id === id)
  if (idx === -1) return
  projects[idx] = { ...projects[idx], ...patch, updatedAt: Date.now() }
  saveProjects(projects)
}

export function renameProject(id: string, name: string) {
  const trimmed = name.trim()
  if (!trimmed) return
  touchProject(id, { name: trimmed })
}

export function deleteProject(id: string) {
  saveProjects(loadProjects().filter((p) => p.id !== id))
  try {
    localStorage.removeItem(canvasKeyFor(id))
  } catch {
    /* ignore */
  }
}

/** Human-friendly "updated 3m ago" string. */
export function relativeTime(ts: number): string {
  const s = Math.max(0, Math.floor((Date.now() - ts) / 1000))
  if (s < 60) return 'just now'
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.floor(h / 24)
  if (d < 7) return `${d}d ago`
  const w = Math.floor(d / 7)
  if (w < 5) return `${w}w ago`
  return `${Math.floor(d / 30)}mo ago`
}
